import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { VoiceModuleHandler } from './voice-handler.interface';
import { VoiceIntent, VoiceExecutionResult } from '../models';
import { VoiceContextService, ProjectMemberInfo } from '../services/voice-context.service';
import { MemberResolverService } from '../services/member-resolver.service';
import { AuthService } from '../../auth/auth.service';
import { TaskService, MyTasksFilters, MyTaskItem } from '../../../modules/project-management/services/task.service';
import { TaskStatusService } from '../../../modules/project-management/services/task-status.service';
import { TaskGroupService } from '../../../modules/project-management/services/task-group.service';
import { ProjectReleaseVersionsService } from '../../../modules/project-management/services/project-release-versions.service';
import { TaskCommentService } from '../../../modules/project-management/services/task-comment.service';
import { ProjectService } from '../../../modules/project-management/services/project.service';
import { ProjectTask } from '../../../modules/project-management/interfaces/project-task.interface';
import { Project } from '../../../modules/project-management/interfaces/project.interface';
import { TaskPriority, TaskStatus } from '../../../modules/project-management/enums';
import { ProjectTag } from '../../../modules/project-management/consts/tags.const';
import { pickVariant } from './response-variants.util';

/** Portée de `project.listMyTasks` (cf. `IntentValidatorService`). */
type MyTasksScope = 'today' | 'overdue' | 'all';

/** Lien vers une tâche, rendu en texte lisible chat/TTS (voir `handleListMyTasks`). */
interface TaskLink {
  taskId: string | undefined;
  label: string;
  route: string;
}

/** Forme brute d'une ligne `project_members` (jointure `profile:profiles(*)`), cf. ProjectService.getProjectMembers */
interface ProjectMemberRow {
  profile_id?: string;
  profile?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ProjectVoiceHandler implements VoiceModuleHandler {
  private router = inject(Router);
  private auth = inject(AuthService);
  private voiceContext = inject(VoiceContextService);
  private memberResolver = inject(MemberResolverService);
  private taskService = inject(TaskService);
  private taskStatusService = inject(TaskStatusService);
  private taskGroupService = inject(TaskGroupService);
  private releaseService = inject(ProjectReleaseVersionsService);
  private taskCommentService = inject(TaskCommentService);
  private projectService = inject(ProjectService);

  private readonly UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /** Nombre max de tâches remontées par `project.listMyTasks` — borne raisonnable pour une synthèse vocale/chat. */
  private readonly MY_TASKS_PAGE_SIZE = 50;

  readonly supportedEndpoints = [
    'project.createTask',
    'project.updateTaskStatus',
    'project.deleteTask',
    'project.createProject',
    'project.assignTask',
    'project.listMembers',
    'project.listMyTasks',
    'project.projectSummary',
    'project.addComment',
    'project.addTagToTask',
  ];

  async execute(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    switch (intent.endpoint) {
      case 'project.createTask':
        return this.handleCreateTask(intent);
      case 'project.updateTaskStatus':
        return this.handleUpdateTaskStatus(intent);
      case 'project.deleteTask':
        return this.handleDeleteTask(intent);
      case 'project.createProject':
        return this.handleCreateProject(intent);
      case 'project.assignTask':
        return this.handleAssignTask(intent);
      case 'project.listMembers':
        return this.handleListMembers(intent);
      case 'project.listMyTasks':
        return this.handleListMyTasks(intent);
      case 'project.projectSummary':
        return this.handleProjectSummary(intent);
      case 'project.addComment':
        return this.handleAddComment(intent);
      case 'project.addTagToTask':
        return this.handleAddTagToTask(intent);
      default:
        return { success: false, message: `Action projet non supportée: ${intent.endpoint}` };
    }
  }

  private async handleCreateTask(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const title = (intent.params['title'] as string).trim();
    const projectName = intent.params['projectName'] as string | undefined;
    const assigneeName = intent.params['assigneeName'] as string | undefined;
    const priority = intent.params['priority'] as string | undefined;
    const description = intent.params['description'] as string | undefined;
    const dueDate = intent.params['dueDate'] as string | undefined;
    // MOKA-208 P2 #3 : sprint et lot posés en contexte (« on travaille sur le
    // sprint 3 ») étaient jusqu'ici acceptés puis ignorés — la tâche était créée
    // hors sprint sans que rien ne le signale.
    const groupName = intent.params['groupName'] as string | undefined;
    const releaseName = intent.params['releaseName'] as string | undefined;

    const projectResult = await this.resolveProjectId(projectName);
    if (!projectResult.success) {
      return {
        success: false,
        message: projectResult.error!,
        needsProjectClarification: projectResult.needsProjectClarification
      };
    }
    const projectId = projectResult.projectId!;
    const projectIdentifier = projectResult.projectIdentifier;

    let assigneeId = profile.id;
    let assigneeLabel = 'vous-même';

    if (assigneeName) {
      const members = await this.resolveProjectMembers(projectId);
      const match = this.memberResolver.resolve(assigneeName, members);

      if (match.confidence !== 'none' && match.memberId) {
        assigneeId = match.memberId;
        assigneeLabel = match.memberName;
      } else {
        assigneeLabel = `vous-même (membre "${assigneeName}" non trouvé)`;
      }
    }

    const statuses = await firstValueFrom(
      this.taskStatusService.getStatusesByProject(projectId, { enabledOnly: true })
    );
    if (statuses.length === 0) {
      return { success: false, message: 'Aucun statut configuré sur ce projet' };
    }
    const defaultStatus = statuses.find(s => s.system_key === 'todo') ?? statuses[0];

    // Statut par défaut du projet : system_key 'todo' si présent, sinon le premier statut actif (tri sort_order)
    const taskData: Partial<ProjectTask> = {
      title,
      status_id: defaultStatus.id,
      priority: (priority as TaskPriority) || TaskPriority.Medium,
      project_id: projectId,
      assignee_id: assigneeId,
      organisation_id: String(profile.organisation_id),
      created_by: profile.id
    };

    if (description) taskData.description = description;
    if (dueDate) taskData.due_date = dueDate;

    // Sprint / lot : rattachement silencieux si le libellé correspond. Un nom
    // qui ne correspond à rien n'empêche PAS la création — la tâche compte plus
    // que son classement — mais le message final le dit, pour que personne ne
    // croie la tâche rangée quelque part où elle n'est pas.
    const unmatched: string[] = [];
    const attached: string[] = [];

    if (groupName) {
      const groupId = await this.resolveGroupId(projectId, groupName);
      if (groupId) {
        taskData.group_id = groupId;
        attached.push(`sprint "${groupName}"`);
      } else {
        unmatched.push(`sprint "${groupName}"`);
      }
    }

    if (releaseName) {
      const releaseId = await this.resolveReleaseId(projectId, releaseName);
      if (releaseId) {
        taskData.release_version_id = releaseId;
        attached.push(`lot "${releaseName}"`);
      } else {
        unmatched.push(`lot "${releaseName}"`);
      }
    }

    const created = await firstValueFrom(this.taskService.createTask(taskData));

    if (projectId !== this.voiceContext.currentProjectId()) {
      const routeParam = projectIdentifier || projectId;
      this.router.navigate(['/project-management', routeParam]);
    }

    const taskNumber = created.task_number ? `#${created.task_number}` : '';
    const numberPrefix = taskNumber ? `${taskNumber} ` : '';
    const numberSuffix = taskNumber ? ` (${taskNumber})` : '';
    const result: VoiceExecutionResult = {
      success: true,
      message: pickVariant([
        `Tâche ${numberPrefix}"${title}" créée et assignée à ${assigneeLabel}`,
        `C'est fait : la tâche ${numberPrefix}"${title}" est créée, assignée à ${assigneeLabel}`,
        `Tâche "${title}"${numberSuffix} ajoutée, pour ${assigneeLabel}`
      ])
        + (attached.length > 0 ? `, dans le ${attached.join(' et le ')}` : '')
        + (unmatched.length > 0 ? ` (${unmatched.join(' et ')} introuvable${unmatched.length > 1 ? 's' : ''} sur ce projet)` : ''),
      data: { taskId: created.id, taskNumber: created.task_number, title }
    };

    // Auto-assignation par défaut (pas d'assigneeName fourni) : proposer un follow-up
    if (!assigneeName) {
      result.followUp = {
        question: 'Voulez-vous l\'assigner à quelqu\'un ?',
        endpoint: 'project.assignTask',
        params: { taskTitle: title, taskId: created.id }
      };
    }

    return result;
  }

  private async handleUpdateTaskStatus(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const taskTitle = (intent.params['taskTitle'] as string).trim();
    const newStatus = intent.params['status'] as TaskStatus;
    const projectName = intent.params['projectName'] as string | undefined;

    const { task, projectId, error, needsProjectClarification } = await this.resolveTaskAndProject(intent, taskTitle, projectName);
    if (!task) {
      return {
        success: false,
        message: error ?? `Aucune tâche trouvée avec le titre "${taskTitle}"`,
        needsProjectClarification
      };
    }

    const statuses = await firstValueFrom(
      this.taskStatusService.getStatusesByProject(projectId!, { enabledOnly: true })
    );
    const targetStatus = statuses.find(s => s.system_key === newStatus)
      ?? statuses.find(s => s.label.toLowerCase() === String(newStatus).toLowerCase());

    if (!targetStatus) {
      return { success: false, message: `Statut "${newStatus}" introuvable sur ce projet` };
    }

    await firstValueFrom(this.taskService.updateTaskStatus(task.id, targetStatus.id));

    return {
      success: true,
      message: pickVariant([
        `Tâche "${task.title}" déplacée en "${targetStatus.label}"`,
        `C'est fait : la tâche "${task.title}" est maintenant en "${targetStatus.label}"`,
        `Statut mis à jour pour "${task.title}" : "${targetStatus.label}"`
      ]),
      data: { taskId: task.id, title: task.title, status: newStatus }
    };
  }

  private async handleDeleteTask(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const taskTitle = (intent.params['taskTitle'] as string).trim();
    const projectName = intent.params['projectName'] as string | undefined;

    const { task, error, needsProjectClarification } = await this.resolveTaskAndProject(intent, taskTitle, projectName);
    if (!task) {
      return {
        success: false,
        message: error ?? `Aucune tâche trouvée avec le titre "${taskTitle}"`,
        needsProjectClarification
      };
    }

    await firstValueFrom(this.taskService.deleteTask(task.id));

    return {
      success: true,
      message: pickVariant([
        `Tâche "${task.title}" supprimée`,
        `C'est fait : la tâche "${task.title}" a été supprimée`,
        `Tâche "${task.title}" retirée du projet`
      ]),
      data: { taskId: task.id, title: task.title }
    };
  }

  private async handleCreateProject(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const rawName = intent.params['name'] as string | undefined;
    const name = rawName?.trim();
    if (!name) {
      return { success: false, message: 'Nom du projet manquant' };
    }

    const description = intent.params['description'] as string | undefined;
    const startDate = intent.params['startDate'] as string | undefined;

    try {
      const projectData: Partial<Project> = {
        // Identifiant généré par la base à partir du nom (MOKA-222).
        name,
        description: description ?? '',
        start_date: startDate ?? this.todayIso(),
        manager_id: profile.id
      };

      const created = await firstValueFrom(this.projectService.createProject(projectData));

      const routeParam = created.identifier ?? created.id;
      this.router.navigate(['/project-management', routeParam]);

      return {
        success: true,
        message: `Projet "${created.name}" créé avec l'identifiant ${created.identifier}`,
        data: { projectId: created.id, identifier: created.identifier, name: created.name }
      };
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'erreur inconnue';
      return { success: false, message: `Erreur lors de la création : ${msg}` };
    }
  }

  private async handleAssignTask(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const taskTitle = (intent.params['taskTitle'] as string).trim();
    const assigneeName = (intent.params['assigneeName'] as string).trim();
    const projectName = intent.params['projectName'] as string | undefined;

    const { task, projectId, error, needsProjectClarification } = await this.resolveTaskAndProject(intent, taskTitle, projectName);
    if (!task) {
      return {
        success: false,
        message: error ?? `Aucune tâche trouvée avec le titre "${taskTitle}"`,
        needsProjectClarification
      };
    }

    const normalizedAssignee = assigneeName.toLowerCase();
    const isSelfAssignment = ['moi', 'me', 'moi-même', 'moi même'].includes(normalizedAssignee);

    let assigneeId: string;
    let assigneeLabel: string;

    if (isSelfAssignment) {
      assigneeId = profile.id;
      assigneeLabel = 'vous-même';
    } else {
      const members = await this.resolveProjectMembers(projectId!);
      const match = this.memberResolver.resolve(assigneeName, members);

      if (match.confidence === 'none' || !match.memberId) {
        return { success: false, message: `Membre "${assigneeName}" non trouvé dans le projet` };
      }

      assigneeId = match.memberId;
      assigneeLabel = match.memberName;
    }

    await firstValueFrom(this.taskService.updateTask(task.id!, { assignee_id: assigneeId }));

    return {
      success: true,
      message: pickVariant([
        `Tâche "${task.title}" assignée à ${assigneeLabel}`,
        `C'est fait : la tâche "${task.title}" est maintenant assignée à ${assigneeLabel}`,
        `Tâche "${task.title}" confiée à ${assigneeLabel}`
      ]),
      data: { taskId: task.id, title: task.title, assigneeId }
    };
  }

  /**
   * MOKA-206 Phase 3, Lot E : liste les membres d'un projet. Réutilise EXACTEMENT
   * le même chemin de résolution que les autres handlers (`resolveProjectId` +
   * `resolveProjectMembers`) — c'est ce qui fait fonctionner automatiquement la
   * clarification "projet manquant" (Lot C/H) dessus aussi, sans code dédié.
   * Message sans puces ni markdown (une phrase, noms séparés par des virgules) :
   * doit rester lisible affiché en chat ET lu tel quel en TTS. Le rôle n'est pas
   * inclus : `resolveProjectMembers` retourne `ProjectMemberInfo` (profileId/
   * firstName/lastName/email), sans champ rôle — pas de complexification pour
   * l'ajouter rien que pour cet endpoint.
   */
  private async handleListMembers(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const projectName = intent.params['projectName'] as string | undefined;

    const projectResult = await this.resolveProjectId(projectName);
    if (!projectResult.success) {
      return {
        success: false,
        message: projectResult.error!,
        needsProjectClarification: projectResult.needsProjectClarification
      };
    }
    const projectId = projectResult.projectId!;

    const members = await this.resolveProjectMembers(projectId);
    const names = members
      .map(m => `${m.firstName} ${m.lastName}`.trim())
      .filter(name => name.length > 0);

    if (names.length === 0) {
      return {
        success: true,
        message: 'Ce projet n\'a aucun membre pour le moment.',
        data: { projectId, members: [] }
      };
    }

    const message = names.length === 1
      ? `Ce projet a un membre : ${names[0]}.`
      : `Ce projet a ${names.length} membres : ${names.join(', ')}.`;

    return {
      success: true,
      message,
      data: { projectId, members: names }
    };
  }

  /**
   * MOKA-207 Phase 4, Lot 1 : tâches assignées à l'utilisateur courant, filtrées
   * par échéance (`scope`) et optionnellement par projet.
   *
   * `scope='overdue'` réutilise directement le filtre serveur `MyTasksFilters.due`
   * de `TaskService.getMyTasksPaginated` (déjà utilisé par la vue "Mes tâches").
   * `scope='today'` n'a pas d'équivalent serveur dédié : on filtre côté client sur
   * `due_date === aujourd'hui` après un chargement non filtré par échéance — la
   * page de `MY_TASKS_PAGE_SIZE` tâches (triées par échéance croissante) couvre
   * largement l'usage vocal réel. `scope='all'` ne pose aucun filtre d'échéance
   * (le filtre "non terminé" reste, lui, toujours actif côté serveur).
   *
   * Rendu des liens : voir la JSDoc de `TaskLink` — pas de support de liens
   * cliquables dans `voice-chat-thread.component.html` au moment de ce lot (le
   * contenu d'un message est un `{{ interpolation }}` texte brut, cf. lecture du
   * fichier). Plutôt que de modifier ce composant partagé — activement retouché
   * en parallèle par d'autres lots (CSS, dark mode, flows guidés...) — chaque
   * tâche est rendue en texte lisible "titre (chemin)" dans `message`, ET les
   * champs structurés (id/label/route) sont exposés dans `data.tasks` pour qu'un
   * futur rendu (markdown/anchor) puisse les consommer sans repasser par le
   * handler. Voir le rapport de fin de tâche pour le détail de ce choix.
   */
  private async handleListMyTasks(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const scope = intent.params['scope'] as MyTasksScope;
    const projectName = intent.params['projectName'] as string | undefined;

    let filterProjectId: string | undefined;
    if (projectName) {
      const projectResult = await this.resolveProjectId(projectName);
      if (!projectResult.success) {
        return {
          success: false,
          message: projectResult.error!,
          needsProjectClarification: projectResult.needsProjectClarification
        };
      }
      filterProjectId = projectResult.projectId;
    }

    const filters: MyTasksFilters = {
      projectId: filterProjectId ?? null,
      due: scope === 'overdue' ? 'overdue' : null
    };

    const { tasks } = await firstValueFrom(
      this.taskService.getMyTasksPaginated(profile.id, 0, this.MY_TASKS_PAGE_SIZE, filters)
    );

    const todayIso = this.todayIso();
    const filtered = scope === 'today' ? tasks.filter(t => t.due_date === todayIso) : tasks;

    const scopeLabel = scope === 'today' ? 'pour aujourd\'hui' : scope === 'overdue' ? 'en retard' : 'ouvertes';

    if (filtered.length === 0) {
      return {
        success: true,
        message: `Vous n'avez aucune tâche ${scopeLabel}.`,
        data: { scope, tasks: [] }
      };
    }

    const links = filtered.map(t => this.toTaskLink(t));
    const intro = `Vous avez ${filtered.length} tâche${filtered.length > 1 ? 's' : ''} ${scopeLabel} :`;
    const listText = links.map(l => `- ${l.label} (${l.route})`).join('\n');

    return {
      success: true,
      message: `${intro}\n${listText}`,
      data: { scope, tasks: links }
    };
  }

  /**
   * Résout un nom de sprint vers son identifiant, insensible à la casse et aux
   * accents (même tolérance que la résolution des étiquettes). `null` si aucun
   * sprint ne correspond : l'appelant décide quoi en faire.
   */
  private async resolveGroupId(projectId: string, groupName: string): Promise<string | null> {
    try {
      const groups = await firstValueFrom(this.taskGroupService.getByProject(projectId));
      const target = this.normalizeLabel(groupName);
      const match = groups.find(g => this.normalizeLabel(g.label) === target)
        ?? groups.find(g => this.normalizeLabel(g.label).includes(target));
      return match?.id ?? null;
    } catch {
      return null;
    }
  }

  /** Équivalent de `resolveGroupId` pour les lots (versions de livraison). */
  private async resolveReleaseId(projectId: string, releaseName: string): Promise<string | null> {
    try {
      const releases = await firstValueFrom(this.releaseService.getByProject(projectId));
      const target = this.normalizeLabel(releaseName);
      const match = releases.find(r => this.normalizeLabel(r.name) === target)
        ?? releases.find(r => this.normalizeLabel(r.name).includes(target));
      return match?.id ?? null;
    } catch {
      return null;
    }
  }

  /** Comparaison de libellés tolérante : casse et accents ignorés. */
  private normalizeLabel(value: string): string {
    return value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /** Construit le lien texte d'une tâche "Mes tâches" (cf. JSDoc de `handleListMyTasks`). */
  private toTaskLink(task: MyTaskItem): TaskLink {
    const identifier = task.project?.identifier ?? task.project_id;
    const label = task.task_number ? `#${task.task_number} ${task.title}` : task.title;
    const route = task.task_number
      ? `/project-management/${identifier}/task/${task.task_number}`
      : `/project-management/${identifier}`;
    return { taskId: task.id, label, route };
  }

  /**
   * MOKA-207 Phase 4, Lot 1 : synthèse d'un projet — compteurs par statut et
   * pourcentage terminé. Réutilise `TaskService.getTasksByProject` (déjà utilisé
   * par `findTaskByTitle`/`resolveProjectMembers` dans ce handler) : chaque tâche
   * y est déjà jointe à son statut complet (`counts_as_done`, `sort_order`), ce
   * qui évite un second appel à `TaskStatusService` rien que pour le regroupement.
   */
  private async handleProjectSummary(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const projectName = intent.params['projectName'] as string | undefined;
    const projectResult = await this.resolveProjectId(projectName);
    if (!projectResult.success) {
      return {
        success: false,
        message: projectResult.error!,
        needsProjectClarification: projectResult.needsProjectClarification
      };
    }
    const projectId = projectResult.projectId!;

    const [project, tasks] = await Promise.all([
      firstValueFrom(this.projectService.getProjectById(projectId)),
      firstValueFrom(this.taskService.getTasksByProject(projectId))
    ]);

    const total = tasks.length;
    if (total === 0) {
      return {
        success: true,
        message: `Le projet "${project.name}" n'a aucune tâche pour le moment.`,
        data: { projectId, total: 0 }
      };
    }

    const doneCount = tasks.filter(t => t.status?.counts_as_done).length;
    const completedPercentage = Math.round((doneCount / total) * 100);

    const byStatus = new Map<string, { label: string; sortOrder: number; count: number }>();
    for (const task of tasks) {
      const status = task.status;
      if (!status) continue;
      const key = status.id ?? status.label;
      const entry = byStatus.get(key);
      if (entry) {
        entry.count++;
      } else {
        byStatus.set(key, { label: status.label, sortOrder: status.sort_order ?? 0, count: 1 });
      }
    }
    const breakdown = [...byStatus.values()]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(s => `${s.count} en "${s.label}"`)
      .join(', ');

    return {
      success: true,
      message: `Le projet "${project.name}" compte ${total} tâche${total > 1 ? 's' : ''} : ${breakdown}. ${completedPercentage}% terminé.`,
      data: { projectId, total, done: doneCount, completedPercentage }
    };
  }

  /**
   * MOKA-207 Phase 4, Lot 1 : ajoute un commentaire à une tâche. `projectName`
   * n'existe pas dans le contrat de cet endpoint (cf. `ALLOWED_ENDPOINTS`) — la
   * résolution tâche/projet passe uniquement par `taskId` (référence conversationnelle
   * déjà résolue), `taskTitle` (recherche par titre sur le projet courant), ou une
   * erreur explicite si aucun des deux n'est fourni.
   */
  private async handleAddComment(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const comment = (intent.params['comment'] as string).trim();
    const taskTitle = (intent.params['taskTitle'] as string | undefined)?.trim();
    const taskId = intent.params['taskId'] as string | undefined;

    if (!taskId && !taskTitle) {
      return { success: false, message: 'Précisez la tâche concernée par ce commentaire.' };
    }

    const { task, error, needsProjectClarification } = await this.resolveTaskAndProject(intent, taskTitle ?? '', undefined);
    if (!task) {
      return {
        success: false,
        message: error ?? `Aucune tâche trouvée avec le titre "${taskTitle}"`,
        needsProjectClarification
      };
    }

    const created = await firstValueFrom(this.taskCommentService.createComment(task.id!, comment));

    return {
      success: true,
      message: pickVariant([
        `Commentaire ajouté à la tâche "${task.title}"`,
        `C'est fait : votre commentaire a été ajouté à "${task.title}"`,
        `Commentaire enregistré sur "${task.title}"`
      ]),
      data: { taskId: task.id, title: task.title, commentId: created.id }
    };
  }

  /**
   * MOKA-207 Phase 4, Lot 1 : ajoute un tag du catalogue du projet (`projects.available_tags`)
   * à une tâche. Matching insensible casse/accents (cf. `normalizeTagText`) contre le
   * `label` PUIS le `slug` du catalogue DU PROJET résolu (jamais un catalogue global) ;
   * échec explicite listant les étiquettes valides si aucun match. `task.tags` peut
   * contenir des entrées `string[]` héritées (cf. `ProjectTask.tags`) — `tagKey` normalise
   * les deux formes pour la détection de doublon.
   */
  private async handleAddTagToTask(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const tagQuery = (intent.params['tag'] as string).trim();
    const taskTitle = (intent.params['taskTitle'] as string | undefined)?.trim();
    const taskId = intent.params['taskId'] as string | undefined;

    if (!taskId && !taskTitle) {
      return { success: false, message: 'Précisez la tâche à laquelle ajouter cette étiquette.' };
    }

    const { task, projectId, error, needsProjectClarification } = await this.resolveTaskAndProject(intent, taskTitle ?? '', undefined);
    if (!task || !projectId) {
      return {
        success: false,
        message: error ?? `Aucune tâche trouvée avec le titre "${taskTitle}"`,
        needsProjectClarification
      };
    }

    const availableTags = await firstValueFrom(this.projectService.getProjectTags(projectId));
    const matchedTag = this.matchTag(tagQuery, availableTags);

    if (!matchedTag) {
      const validTags = availableTags.map(t => t.label).join(', ');
      return {
        success: false,
        message: availableTags.length > 0
          ? `Étiquette "${tagQuery}" introuvable. Étiquettes disponibles : ${validTags}.`
          : 'Ce projet n\'a aucune étiquette configurée.'
      };
    }

    const existingTags: unknown[] = task.tags ?? [];
    const alreadyTagged = existingTags.some(t => this.tagKey(t) === this.normalizeTagText(matchedTag.slug));
    if (alreadyTagged) {
      return {
        success: true,
        message: `La tâche "${task.title}" a déjà l'étiquette "${matchedTag.label}"`,
        data: { taskId: task.id, tag: matchedTag.slug }
      };
    }

    await firstValueFrom(this.taskService.updateTask(task.id!, { tags: [...existingTags, matchedTag] }));

    return {
      success: true,
      message: pickVariant([
        `Étiquette "${matchedTag.label}" ajoutée à la tâche "${task.title}"`,
        `C'est fait : "${task.title}" porte maintenant l'étiquette "${matchedTag.label}"`,
        `Tâche "${task.title}" étiquetée "${matchedTag.label}"`
      ]),
      data: { taskId: task.id, title: task.title, tag: matchedTag.slug }
    };
  }

  /** Résout une étiquette dictée contre le catalogue du projet — label exact, puis slug exact, puis inclusion partielle. */
  private matchTag(query: string, tags: ProjectTag[]): ProjectTag | null {
    const normalizedQuery = this.normalizeTagText(query);
    return tags.find(t => this.normalizeTagText(t.label) === normalizedQuery)
      ?? tags.find(t => this.normalizeTagText(t.slug) === normalizedQuery)
      ?? tags.find(t => {
        const normalizedLabel = this.normalizeTagText(t.label);
        return normalizedLabel.includes(normalizedQuery) || normalizedQuery.includes(normalizedLabel);
      })
      ?? null;
  }

  /** Clé de comparaison d'une entrée `task.tags` (objet `ProjectTag` OU chaîne héritée). */
  private tagKey(tag: unknown): string {
    if (typeof tag === 'string') return this.normalizeTagText(tag);
    const obj = tag as { slug?: string; label?: string };
    return this.normalizeTagText(obj.slug ?? obj.label ?? '');
  }

  /** Normalise casse et accents pour une comparaison tolérante (dictée vocale). */
  private normalizeTagText(value: string): string {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  private todayIso(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private async resolveProjectId(projectName?: string): Promise<{
    success: boolean;
    projectId?: string;
    projectIdentifier?: string;
    error?: string;
    needsProjectClarification?: boolean;
  }> {
    if (projectName) {
      try {
        const project = await firstValueFrom(
          this.projectService.getProjectByIdentifier(projectName)
        );
        if (project?.id) {
          return { success: true, projectId: project.id, projectIdentifier: project.identifier };
        }
      } catch {
        // Identifier non trouvé, essayer par nom via recherche
      }

      try {
        const projects = await firstValueFrom(
          this.projectService.searchProjectsByName(projectName)
        );
        const match = projects?.find(
          (p) => p.name.toLowerCase() === projectName.toLowerCase()
        ) || projects?.[0];

        if (match?.id) {
          return { success: true, projectId: match.id, projectIdentifier: match.identifier };
        }
      } catch {
        // Recherche échouée
      }

      const contextProjectId = this.voiceContext.currentProjectId();
      if (contextProjectId) {
        return { success: true, projectId: contextProjectId };
      }

      return { success: false, error: `Projet "${projectName}" non trouvé` };
    }

    const contextProjectId = this.voiceContext.currentProjectId();
    if (contextProjectId) {
      return { success: true, projectId: contextProjectId };
    }

    return {
      success: false,
      error: 'Aucun projet spécifié. Précisez le nom du projet ou naviguez sur une page projet.',
      needsProjectClarification: true
    };
  }

  /**
   * Résout la tâche ET le projet visés par l'intent.
   *
   * Ordre de résolution : si `intent.params.taskId` est un uuid valide (transmis par
   * GPT lorsqu'une référence conversationnelle a été résolue côté contexte session,
   * ex. « cette tâche » → lastTask, ou un follow-up restauré), la tâche est résolue
   * EN PREMIER par id via `taskService.getTaskById`, et le projet en est dérivé
   * (`task.project_id`) — `resolveProjectId` n'est alors pas appelé, ce qui évite un
   * échec prématuré « Aucun projet spécifié » quand aucun projet n'est affiché/précisé
   * (ex. reprise de session depuis le dashboard).
   *
   * Sinon (pas de `taskId` valide), comportement historique inchangé : le projet est
   * résolu via `resolveProjectId(projectName)` puis la tâche par recherche floue sur
   * le titre via `findTaskByTitle`.
   */
  private async resolveTaskAndProject(
    intent: VoiceIntent,
    taskTitle: string,
    projectName?: string
  ): Promise<{
    task: ProjectTask | null;
    projectId?: string;
    projectIdentifier?: string;
    error?: string;
    needsProjectClarification?: boolean;
  }> {
    const taskId = intent.params['taskId'];
    if (typeof taskId === 'string' && this.UUID_REGEX.test(taskId)) {
      try {
        const task = await firstValueFrom(this.taskService.getTaskById(taskId));
        return { task, projectId: task.project_id };
      } catch {
        return { task: null, error: 'La tâche référencée n\'existe plus.' };
      }
    }

    const projectResult = await this.resolveProjectId(projectName);
    if (!projectResult.success) {
      return { task: null, error: projectResult.error, needsProjectClarification: projectResult.needsProjectClarification };
    }
    const projectId = projectResult.projectId!;

    const task = await this.findTaskByTitle(projectId, taskTitle);
    return { task, projectId, projectIdentifier: projectResult.projectIdentifier };
  }

  /**
   * Résout les membres du projet RÉSOLU (celui de la tâche ou de `resolveProjectId`),
   * pas ceux de la page actuellement affichée (`voiceContext.projectMembers()` ne
   * contient que le projet visible dans l'URL — vide sur le dashboard, ou trompeur si
   * la commande vise un projet différent). Reproduit le mapping de
   * `VoiceContextService.loadProjectContext()`. Fallback sur `voiceContext.projectMembers()`
   * si le chargement échoue ; l'échec explicite « membre non trouvé » reste inchangé
   * si le membre n'y est toujours pas.
   */
  private async resolveProjectMembers(projectId: string): Promise<ProjectMemberInfo[]> {
    try {
      const members = await firstValueFrom(this.projectService.getProjectMembers(projectId));
      return (members as ProjectMemberRow[])
        .filter((m) => !!m.profile)
        .map((m) => ({
          profileId: m.profile_id || m.profile?.id || '',
          firstName: m.profile?.first_name || '',
          lastName: m.profile?.last_name || '',
          email: m.profile?.email || ''
        }));
    } catch {
      return this.voiceContext.projectMembers();
    }
  }

  private async findTaskByTitle(projectId: string, title: string): Promise<ProjectTask | null> {
    const tasks = await firstValueFrom(this.taskService.getTasksByProject(projectId));
    const normalized = title.toLowerCase();

    const exact = tasks.find(t => t.title.toLowerCase() === normalized);
    if (exact) return exact;

    const includes = tasks.filter(t => t.title.toLowerCase().includes(normalized));
    if (includes.length === 1) return includes[0];

    const reverse = tasks.filter(t => normalized.includes(t.title.toLowerCase()));
    if (reverse.length === 1) return reverse[0];

    if (includes.length > 1) {
      includes.sort((a, b) =>
        Math.abs(a.title.length - title.length) - Math.abs(b.title.length - title.length)
      );
      return includes[0];
    }

    return null;
  }
}
